import * as React from "react"
import { Link as RouterLink } from "react-router"
import { cn } from "@/lib/utils"

type LinkBaseProps = {
  className?: string
  children?: React.ReactNode
}

type InternalLinkProps = LinkBaseProps & {
  to: string
  href?: never
  external?: false
} & Omit<React.ComponentProps<typeof RouterLink>, "to" | "className" | "children">

type ExternalLinkProps = LinkBaseProps & {
  href: string
  to?: never
  external?: true
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "className" | "children">

type LinkProps = InternalLinkProps | ExternalLinkProps

function linkClassName(className?: string) {
  return cn(
    "text-inherit underline underline-offset-2 hover:opacity-80",
    className,
  )
}

/**
 * App link primitive. Internal routes go through react-router;
 * external URLs render as `<a rel="noopener noreferrer">`.
 */
function Link(props: LinkProps) {
  if ("to" in props && props.to != null) {
    const { to, className, children, ...rest } = props
    return (
      <RouterLink to={to} data-slot="link" className={linkClassName(className)} {...rest}>
        {children}
      </RouterLink>
    )
  }

  const { href, className, children, ...rest } = props
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-slot="link"
      className={linkClassName(className)}
      {...rest}
    >
      {children}
    </a>
  )
}

export { Link }
export type { LinkProps }
